import { useEffect, useState } from 'react'
import { api, guardarSessao, sessaoAtual, type Sessao } from './api.js'

/// Quem é o usuário **agora**, e não quem ele era no dia em que fez login.
///
/// A sessão guardada no aparelho carrega uma cópia dos perfis, tirada no momento
/// do login e válida por 30 dias. Isso é o que faz o app funcionar sem rede — e é
/// exatamente o que não serve para decidir o que a tela pode oferecer: um perfil
/// concedido ou retirado hoje não aparece até a pessoa entrar de novo, um mês
/// depois.
///
/// Aqui o valor sai na hora do que está guardado (funciona offline, sem piscar) e
/// é atualizado com o servidor assim que houver rede. A sessão local é regravada
/// junto, então a próxima abertura já começa certa.
///
/// A tela não é a trava: o servidor recusa de qualquer jeito. Isto existe para a
/// interface não oferecer o que vai dar erro, nem esconder o que a pessoa pode.

export interface UsuarioAtual {
  id: string
  email: string
  nome: string
  perfis: string[]
  admin: boolean
  superAdmin: boolean
  vetodasObras: boolean
}

export function useUsuarioAtual(): { usuario: UsuarioAtual | null; carregando: boolean } {
  const [usuario, setUsuario] = useState<UsuarioAtual | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    let vivo = true

    void (async () => {
      const guardada = await sessaoAtual()
      if (!vivo) return
      if (guardada) {
        // Sessão antiga não tem `superAdmin`; assume falso até o servidor dizer.
        setUsuario({
          ...guardada.usuario,
          superAdmin: guardada.usuario.superAdmin ?? false,
        } as UsuarioAtual)
      }
      setCarregando(false)

      if (!guardada) return
      try {
        const fresco = await api.eu()
        if (!vivo) return
        setUsuario(fresco as UsuarioAtual)
        // Regrava a sessão para que a próxima abertura, mesmo offline, já saiba
        // dos perfis de hoje.
        const atualizada: Sessao = {
          ...guardada,
          usuario: {
            ...guardada.usuario,
            perfis: fresco.perfis,
            admin: fresco.admin,
            superAdmin: fresco.superAdmin,
            vetodasObras: fresco.vetodasObras,
          },
        }
        await guardarSessao(atualizada)
      } catch {
        // Sem rede, ou sessão expirada: fica com o que estava guardado. Não é erro
        // que valha mostrar — o app inteiro é feito para trabalhar assim.
      }
    })()

    return () => {
      vivo = false
    }
  }, [])

  return { usuario, carregando }
}
